/**
 * iter 156 — Two-Factor Authentication OTP verification dialog.
 *
 * Renders 6 single-character input cells with auto-advance + paste support.
 * Honours the backend `cooldown_seconds` for the Resend button via a
 * live-updating countdown.
 *
 * Props:
 *   • open                  boolean
 *   • email                 string (display only)
 *   • challengeId           string (opaque server token)
 *   • cooldownSeconds       number   default 60
 *   • expiresAt             ISO string   used to show "Code expires in X:XX"
 *   • onVerified(userData)  called after successful /auth/verify-otp
 *   • onCancel()            close handler (go back to login form)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ShieldCheck, RotateCw, Loader2, ArrowLeft } from 'lucide-react';

const OTP_LENGTH = 6;

function _formatRemaining(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function OtpVerifyDialog({
  open,
  email,
  challengeId,
  cooldownSeconds = 60,
  expiresAt,
  onVerified,
  onCancel,
}) {
  const { verifyOtp, resendOtp } = useAuth();
  const [digits, setDigits] = useState(() => Array(OTP_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(cooldownSeconds);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [currentChallengeId, setCurrentChallengeId] = useState(challengeId);
  const [currentExpiresAt, setCurrentExpiresAt] = useState(expiresAt);
  // iter 167 — "Remember this device" — checked by default per product spec.
  // When true, the backend stores a trusted-device row valid for 90 days so
  // subsequent logins from this browser skip the OTP step.
  const [rememberDevice, setRememberDevice] = useState(true);
  const inputs = useRef([]);

  // Reset when the dialog opens / challenge changes
  useEffect(() => {
    if (!open) return;
    setDigits(Array(OTP_LENGTH).fill(''));
    setError('');
    setCurrentChallengeId(challengeId);
    setCurrentExpiresAt(expiresAt);
    setResendIn(cooldownSeconds);
    // Auto-focus the first cell shortly after the dialog paints
    const t = setTimeout(() => inputs.current[0]?.focus(), 80);
    return () => clearTimeout(t);
  }, [open, challengeId, expiresAt, cooldownSeconds]);

  // Resend countdown ticker
  useEffect(() => {
    if (!open || resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open, resendIn]);

  // Expiry ticker
  const expiresInSec = useMemo(() => {
    if (!currentExpiresAt) return null;
    const ms = new Date(currentExpiresAt).getTime() - Date.now();
    return Math.max(0, Math.floor(ms / 1000));
  }, [currentExpiresAt]);
  const [expiryTick, setExpiryTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setExpiryTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [open]);
  const expiryRemaining = useMemo(() => {
    if (!currentExpiresAt) return null;
    const ms = new Date(currentExpiresAt).getTime() - Date.now();
    return Math.max(0, Math.floor(ms / 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExpiresAt, expiryTick]);

  const setDigit = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    setDigits((prev) => {
      const next = [...prev];
      next[i] = val;
      return next;
    });
    setError('');
    if (val && i < OTP_LENGTH - 1) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').trim();
    if (!/^\d{6}$/.test(text)) return;
    e.preventDefault();
    setDigits(text.split(''));
    inputs.current[OTP_LENGTH - 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    } else if (e.key === 'Enter') {
      submit();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      inputs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) {
      inputs.current[i + 1]?.focus();
    }
  };

  const code = digits.join('');
  const isComplete = code.length === OTP_LENGTH;

  const submit = async () => {
    if (!isComplete || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const user = await verifyOtp(currentChallengeId, code, { rememberDevice });
      toast.success('Verification successful — welcome back!');
      onVerified?.(user);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Incorrect code. Please try again.';
      setError(detail);
      // Clear digits when the server says "Code expired" so the user can't
      // re-submit the same dead code.
      if (/expired/i.test(detail)) {
        setDigits(Array(OTP_LENGTH).fill(''));
        inputs.current[0]?.focus();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      const res = await resendOtp(currentChallengeId);
      setCurrentChallengeId(res.challenge_id || currentChallengeId);
      setCurrentExpiresAt(res.expires_at || currentExpiresAt);
      setResendIn(res.cooldown_seconds || 60);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputs.current[0]?.focus();
      toast.success('A new code has been sent.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not resend code.';
      setError(detail);
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="otp-verify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Verify it's you
          </DialogTitle>
          <DialogDescription className="leading-relaxed">
            We sent a 6-digit code to <span className="font-semibold text-slate-800">{email}</span>.
            Enter it below to finish signing in. The code expires in 5 minutes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value.slice(-1))}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={submitting}
                className={`w-12 h-14 text-center text-2xl font-semibold rounded-lg border-2 ${error ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'} focus:outline-none transition-colors`}
                data-testid={`otp-cell-${i}`}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {expiryRemaining !== null && (
            <p className="text-center text-xs text-slate-500" data-testid="otp-expiry">
              Code expires in <span className="font-medium text-slate-700">{_formatRemaining(expiryRemaining)}</span>
            </p>
          )}

          {error && (
            <p className="text-sm text-red-600 text-center" data-testid="otp-error">{error}</p>
          )}

          {/* iter 167 — "Remember this device" — checked by default. */}
          <label
            className="flex items-start gap-2 text-sm text-slate-700 select-none cursor-pointer px-1"
            data-testid="otp-remember-device-row"
          >
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
              data-testid="otp-remember-device-checkbox"
            />
            <span>
              <span className="font-medium text-slate-800">Remember this device for 90 days</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Skip the verification code on this browser until you log out or change your password.
              </span>
            </span>
          </label>

          <Button
            onClick={submit}
            disabled={!isComplete || submitting}
            className="w-full h-11 bg-[#1E3A8A] hover:bg-[#1e40af] text-base font-medium"
            data-testid="otp-submit-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Verifying…</span>
            ) : (
              <span>Verify &amp; Sign In</span>
            )}
          </Button>

          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              data-testid="otp-back-btn"
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to sign-in
            </Button>
            <Button
              variant="link"
              size="sm"
              disabled={resendIn > 0 || resending}
              onClick={handleResend}
              data-testid="otp-resend-btn"
              className="text-blue-600"
            >
              {resending ? (
                <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Sending…</span>
              ) : resendIn > 0 ? (
                <span className="text-slate-400">Resend in {resendIn}s</span>
              ) : (
                <span className="flex items-center gap-1"><RotateCw className="w-3 h-3" />Resend code</span>
              )}
            </Button>
          </div>
        </div>

        <DialogFooter className="text-xs text-slate-400 justify-center">
          Didn't get the email? Check spam, then click Resend.
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OtpVerifyDialog;
